import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowClockwise, Handshake, DownloadSimple, Plus, PencilSimple, Trash, FilePdf, ClockCounterClockwise, Coins, UsersThree, Funnel, CaretDown } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import EmptyState from "@/components/EmptyState";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);
const EMPTY = { name: "", party_id: "", commission_rate: "", mode_names: [], rate_effective_from: "" };

export default function MediatorCommissionReport() {
    const [rows, setRows] = useState([]);
    const [parties, setParties] = useState([]);
    const [modes, setModes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [fromP, setFromP] = useState("");
    const [toP, setToP] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [detail, setDetail] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailExporting, setDetailExporting] = useState(false);
    const [rateHist, setRateHist] = useState(null);
    const [commOpen, setCommOpen] = useState(false);
    const [commSaving, setCommSaving] = useState(false);
    const [commForm, setCommForm] = useState(null); // { mediatorId, mediatorName, allowed, linkedPartyId, party_id, amount, date, utr, remark }
    const [partyMgr, setPartyMgr] = useState(null);
    const [partyPick, setPartyPick] = useState({ party_id: "", effective_from: new Date().toISOString().slice(0, 10) });
    const [partySaving, setPartySaving] = useState(false);
    const [modeSettings, setModeSettings] = useState([]);
    const [modeSaving, setModeSaving] = useState(false);
    const [modeAddOpen, setModeAddOpen] = useState(false);
    const [modeAddSaving, setModeAddSaving] = useState(false);
    const [modeAddForm, setModeAddForm] = useState({ mediator_id: "", mode: "", rate: "", effective_from: new Date().toISOString().slice(0, 10) });
    const [allModes, setAllModes] = useState([]);
    const [selectedModes, setSelectedModes] = useState(null); // null = all modes
    const [totals, setTotals] = useState({ receipts: 0, commission: 0 });

    const openDetail = async (m, mo) => {
        setDetail({ mediatorId: m.id, name: m.name, period: mo.period, label: mo.label, rate: mo.rate });
        setDetailData(null); setDetailLoading(true);
        try {
            const r = await api.get(`/mediators/${m.id}/commission-detail`, { params: { period: mo.period, rate: mo.rate, modes: selectedModes === null ? undefined : (selectedModes.length ? selectedModes.join(",") : "__none__") } });
            setDetailData(r.data);
        } catch (e) { toast.error(formatApiError(e)); setDetailData({ rows: [] }); }
        finally { setDetailLoading(false); }
    };
    const exportDetail = async (kind) => {
        if (!detail) return;
        setDetailExporting(true);
        try {
            const res = await api.get(`/mediators/${detail.mediatorId}/commission-detail.${kind}`, { params: { period: detail.period, rate: detail.rate, modes: selectedModes === null ? undefined : (selectedModes.length ? selectedModes.join(",") : "__none__") }, responseType: "blob" });
            const url = URL.createObjectURL(res.data); const a = document.createElement("a");
            a.href = url; a.download = `commission-${detail.name}-${detail.period}.${kind}`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDetailExporting(false); }
    };
    const toggleReconcile = async (row) => {
        if (!row.id) return;
        const next = !row.reconciled;
        try {
            await api.post(`/transactions/${row.id}/reconcile`, { reconciled: next });
            setDetailData((d) => ({ ...d, rows: d.rows.map((x) => x.id === row.id ? { ...x, reconciled: next } : x) }));
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const removeMediator = async (m) => {
        if (!window.confirm(`Remove mediator "${m.name}" from commission tracking?\n\nThis does NOT delete the party ledger — it only stops commission tracking for this mediator.`)) return;
        try { await api.delete(`/mediators/${m.id}`); toast.success("Mediator removed"); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const openAddCommission = (m) => {
        const allowed = m.applies_to_parties || [];
        setCommForm({
            mediatorId: m.id, mediatorName: m.name,
            allowed, linkedPartyId: m.party_id, linkedPartyName: m.party_name,
            party_id: allowed.length ? "" : (m.party_id || ""),
            amount: "", date: new Date().toISOString().slice(0, 10),
            utr: "Commission", remark: "Commission",
        });
        setCommOpen(true);
    };
    const saveCommission = async () => {
        if (!commForm) return;
        if (!commForm.party_id) return toast.error("Select which party to charge");
        const amt = Number(commForm.amount);
        if (Number.isNaN(amt) || amt <= 0) return toast.error("Enter a commission amount greater than 0");
        setCommSaving(true);
        try {
            await api.post(`/mediators/${commForm.mediatorId}/commission`, {
                amount: amt, date: commForm.date, utr: commForm.utr, remark: commForm.remark, party_id: commForm.party_id,
            });
            toast.success("Commission added to the ledger");
            setCommOpen(false); setCommForm(null);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCommSaving(false); }
    };

    const mapNames = (list) => (list || []).map((ap) => ({ ...ap, party_name: ap.party_name || (parties.find((p) => p.id === ap.party_id)?.name) || ap.party_id }));
    const openPartyMgr = (m) => { setPartyMgr(m); setPartyPick({ party_id: "", effective_from: new Date().toISOString().slice(0, 10) }); };
    const addParty = async () => {
        if (!partyMgr || !partyPick.party_id) return toast.error("Select a party");
        setPartySaving(true);
        try {
            const { data } = await api.post(`/mediators/${partyMgr.id}/parties`, { party_id: partyPick.party_id, effective_from: partyPick.effective_from });
            setPartyMgr({ ...partyMgr, applies_to_parties: mapNames(data.applies_to_parties) });
            setPartyPick({ party_id: "", effective_from: new Date().toISOString().slice(0, 10) });
            toast.success("Party added");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPartySaving(false); }
    };
    const removeParty = async (pid) => {
        if (!partyMgr) return;
        try {
            const { data } = await api.delete(`/mediators/${partyMgr.id}/parties/${pid}`);
            setPartyMgr({ ...partyMgr, applies_to_parties: mapNames(data.applies_to_parties) });
            toast.success("Party removed");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const qs = () => {
        const p = new URLSearchParams();
        if (fromP) p.set("from_period", fromP);
        if (toP) p.set("to_period", toP);
        if (selectedModes !== null) p.set("modes", selectedModes.length ? selectedModes.join(",") : "__none__");
        const s = p.toString();
        return s ? `?${s}` : "";
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/mediators/commission-report${qs()}`);
            setRows(data.rows || []);
            setTotals(data.totals || { receipts: 0, commission: 0 });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    const loadRefs = async () => {
        try {
            const [p, md, ms] = await Promise.all([api.get("/parties"), api.get("/payment-modes"), api.get("/mediators/mode-settings")]);
            setParties((p.data || []).filter((x) => x.active !== false));
            setModes((md.data || []).map((x) => x.name).filter(Boolean));
            setModeSettings((ms.data || []).map((r) => ({ ...r, effective_from: new Date().toISOString().slice(0, 10) })));
            setAllModes([...new Set((ms.data || []).map((r) => r.mode))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })));
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const curSel = selectedModes === null ? allModes : selectedModes;
    const toggleFilterMode = (mode) => {
        const base = selectedModes === null ? allModes : selectedModes;
        setSelectedModes(base.includes(mode) ? base.filter((x) => x !== mode) : [...base, mode]);
    };
    const toggleAllFilterModes = (checked) => setSelectedModes(checked ? [...allModes] : []);

    const setModeRow = (i, patch) => setModeSettings((prev) => prev.map((r, idx) => idx === i ? { ...r, ...patch, _dirty: true } : r));
    const saveModeSettings = async () => {
        const dirty = modeSettings.filter((r) => r._dirty);
        if (!dirty.length) return toast.info("No changes to save");
        setModeSaving(true);
        try {
            for (const r of dirty) {
                await api.put("/mediators/mode-settings", {
                    mediator_id: r.mediator_id, mode: r.mode, enabled: !!r.enabled,
                    rate: (r.rate === "" || r.rate === null || r.rate === undefined) ? null : Number(r.rate),
                    effective_from: r.effective_from || undefined,
                });
            }
            toast.success("Payment mode settings saved");
            const ms = await api.get("/mediators/mode-settings");
            setModeSettings((ms.data || []).map((r) => ({ ...r, effective_from: new Date().toISOString().slice(0, 10) })));
            setAllModes([...new Set((ms.data || []).map((r) => r.mode))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })));
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setModeSaving(false); }
    };

    const mediatorOpts = [...new Map(modeSettings.map((r) => [r.mediator_id, r.mediator_name])).entries()].map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    const openModeAdd = () => {
        const first = modeSettings[0];
        setModeAddForm({ mediator_id: first ? first.mediator_id : "", mode: "", rate: "", effective_from: new Date().toISOString().slice(0, 10) });
        setModeAddOpen(true);
    };
    const saveModeAdd = async () => {
        if (!modeAddForm.mediator_id) return toast.error("Select a mediator");
        if (!modeAddForm.mode.trim()) return toast.error("Enter a payment mode name");
        const rate = Number(modeAddForm.rate);
        if (Number.isNaN(rate) || rate <= 0 || rate > 100) return toast.error("Commission rate must be greater than 0");
        setModeAddSaving(true);
        try {
            await api.post("/mediators/modes", { mediator_id: modeAddForm.mediator_id, mode: modeAddForm.mode.trim(), rate, effective_from: modeAddForm.effective_from });
            toast.success("Payment mode added");
            setModeAddOpen(false);
            const ms = await api.get("/mediators/mode-settings");
            setModeSettings((ms.data || []).map((r) => ({ ...r, effective_from: new Date().toISOString().slice(0, 10) })));
            setAllModes([...new Set((ms.data || []).map((r) => r.mode))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })));
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setModeAddSaving(false); }
    };
    const deleteMode = async (r) => {
        if (!window.confirm(`Remove payment mode "${r.mode}" from ${r.mediator_name}? This unlinks it from the mediator's commission (transactions are not deleted).`)) return;
        try {
            await api.delete(`/mediators/${r.mediator_id}/modes`, { params: { mode: r.mode } });
            toast.success(`Removed "${r.mode}"`);
            const ms = await api.get("/mediators/mode-settings");
            setModeSettings((ms.data || []).map((x) => ({ ...x, effective_from: new Date().toISOString().slice(0, 10) })));
            setAllModes([...new Set((ms.data || []).map((x) => x.mode))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })));
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); loadRefs(); }, []);  // eslint-disable-line

    const exportXlsx = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/mediators/commission-report.xlsx${qs()}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = "mediator-commission-report.xlsx";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDownloading(false); }
    };

    const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
    const openEdit = (m) => {
        setEditing(m);
        setForm({
            name: m.name || "", party_id: m.party_id || "",
            commission_rate: String(m.commission_rate ?? ""),
            mode_names: Array.isArray(m.mode_names) ? m.mode_names : (m.mode_name ? [m.mode_name] : []),
            rate_effective_from: new Date().toISOString().slice(0, 10),
        });
        setDialogOpen(true);
    };

    const toggleMode = (val) => setForm((f) => ({
        ...f, mode_names: f.mode_names.includes(val) ? f.mode_names.filter((x) => x !== val) : [...f.mode_names, val],
    }));

    const save = async () => {
        if (!form.name.trim()) return toast.error("Mediator name is required");
        if (!form.party_id) return toast.error("Pick a linked party");
        const rate = Number(form.commission_rate);
        if (Number.isNaN(rate) || rate <= 0 || rate > 100) return toast.error("Commission rate must be greater than 0");
        if (!form.mode_names.length) return toast.error("Select at least one transaction mode");
        const rateChanged = editing && Number(editing.commission_rate ?? 0) !== rate;
        if (rateChanged) {
            const eff = form.rate_effective_from || new Date().toISOString().slice(0, 10);
            const effDisp = eff.split("-").reverse().join("-");
            if (!window.confirm(`This rate change will affect commission calculation from ${effDisp}. Already posted months will not be changed.`)) return;
        }
        setSaving(true);
        try {
            const payload = { name: form.name.trim(), party_id: form.party_id, commission_rate: rate, mode_names: form.mode_names };
            if (editing && rateChanged) payload.rate_effective_from = form.rate_effective_from || new Date().toISOString().slice(0, 10);
            if (editing) await api.patch(`/mediators/${editing.id}`, payload);
            else await api.post("/mediators", payload);
            toast.success(editing ? "Mediator updated" : "Mediator added");
            setDialogOpen(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const toggleActive = async (m) => {
        try { await api.patch(`/mediators/${m.id}`, { active: !m.active }); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const modeOwner = {};
    rows.forEach((r) => {
        if (editing && r.id === editing.id) return;
        (r.mode_names && r.mode_names.length ? r.mode_names : (r.mode_name ? [r.mode_name] : [])).forEach((mn) => { modeOwner[mn.toLowerCase()] = r.name; });
    });

    return (
        <div className="space-y-6" data-testid="mediator-commission-report-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md">
                            <Handshake size={20} weight="fill" />
                        </div>
                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Mediator Commission</h1>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">Monthly receipts total and rate per mediator. Auto-posting is off — use <b>Add Commission</b> to post a commission entry manually. Use <b>Parties</b> to control which parties a mediator's commission may be charged to (each from an effective date). Unposted months show an estimate for reference only.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <input type="month" data-testid="mcr-from" value={fromP} onChange={(e) => setFromP(e.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-sm" />
                    <span className="text-slate-400">–</span>
                    <input type="month" data-testid="mcr-to" value={toP} onChange={(e) => setToP(e.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-sm" />
                    {allModes.length > 0 && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <button data-testid="mcr-mode-filter" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm hover:bg-slate-100">
                                    <Funnel size={14} /> {selectedModes === null || selectedModes.length === allModes.length ? "All modes" : `${selectedModes.length} of ${allModes.length} modes`}
                                    <CaretDown size={12} />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" className="w-64 p-0" onCloseAutoFocus={() => load()}>
                                <div className="max-h-72 overflow-y-auto p-2">
                                    <label className="flex items-center gap-2 rounded px-2 py-1.5 text-sm font-semibold hover:bg-slate-50">
                                        <input type="checkbox" data-testid="mcr-mode-filter-all" checked={curSel.length === allModes.length && allModes.length > 0} onChange={(e) => toggleAllFilterModes(e.target.checked)} className="h-4 w-4 accent-teal-600" />
                                        Select all
                                    </label>
                                    <div className="my-1 border-t border-slate-100" />
                                    {allModes.map((mode) => (
                                        <label key={mode} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
                                            <input type="checkbox" data-testid={`mcr-mode-filter-${mode}`} checked={curSel.includes(mode)} onChange={() => toggleFilterMode(mode)} className="h-4 w-4 accent-teal-600" />
                                            <span className="font-mono text-xs text-slate-700">{mode}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="flex justify-end border-t border-slate-100 p-2">
                                    <button onClick={() => load()} data-testid="mcr-mode-filter-apply" className="rounded-md bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700">Apply filter</button>
                                </div>
                            </PopoverContent>
                        </Popover>
                    )}
                    <button onClick={load} data-testid="mcr-apply" className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-slate-100 px-3 text-sm font-semibold hover:bg-slate-200">Apply</button>
                    <button onClick={load} disabled={loading} data-testid="mcr-refresh" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm hover:bg-slate-100 disabled:opacity-50">
                        <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                    <button onClick={exportXlsx} disabled={downloading || rows.length === 0} data-testid="mcr-export" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
                        <DownloadSimple size={14} /> {downloading ? "Preparing…" : "Export to Excel"}
                    </button>
                    <Button onClick={openAdd} data-testid="mcr-add-mediator"><Plus size={15} className="mr-1.5" /> Add Mediator</Button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-white px-5 py-3 shadow-sm" data-testid="mcr-totals">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total Receipts</div>
                    <div className="font-display text-xl font-bold text-slate-900" data-testid="mcr-total-receipts">{fmt(totals.receipts)}</div>
                </div>
                <div className="h-8 w-px bg-slate-200" />
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total Commission</div>
                    <div className="font-display text-xl font-bold text-teal-700" data-testid="mcr-total-commission">{fmt(totals.commission)}</div>
                </div>
                {selectedModes !== null && allModes.length > 0 && selectedModes.length !== allModes.length ? (
                    <span className="ml-auto rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800" data-testid="mcr-filter-badge">Filtered: {selectedModes.length} of {allModes.length} modes</span>
                ) : null}
            </div>

            {modeSettings.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="mode-commission-panel">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
                        <div>
                            <div className="font-display text-lg font-bold text-slate-900">Payment Mode Commission Control</div>
                            <p className="text-[11px] text-slate-500">Tick a mode to include it in mediator commission and set its rate. Unticked modes are skipped entirely when the report is calculated. Changing a rate applies from its Effective From date onward — earlier receipts keep the previous rate.</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" onClick={openModeAdd} data-testid="mode-add-open"><Plus size={15} className="mr-1.5" /> Add Payment Mode</Button>
                            <Button onClick={saveModeSettings} disabled={modeSaving} data-testid="mode-settings-save">{modeSaving ? "Saving…" : "Save mode settings"}</Button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-white text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="w-16 px-4 py-2 text-center">On</th>
                                    <th className="px-4 py-2 text-left">Payment Mode</th>
                                    <th className="px-4 py-2 text-left">Mediator / Party</th>
                                    <th className="w-40 px-4 py-2 text-right">Commission %</th>
                                    <th className="w-44 px-4 py-2 text-left">Effective From</th>
                                    <th className="w-16 px-4 py-2 text-center">Delete</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {modeSettings.map((r, i) => (
                                    <tr key={`${r.mediator_id}-${r.mode}`} data-testid={`mode-row-${r.mode}`} className={r.enabled ? "" : "bg-slate-50/60"}>
                                        <td className="px-4 py-2 text-center">
                                            <input type="checkbox" data-testid={`mode-enabled-${r.mode}`} checked={!!r.enabled} onChange={(e) => setModeRow(i, { enabled: e.target.checked })} className="h-4 w-4 cursor-pointer accent-teal-600" />
                                        </td>
                                        <td className="px-4 py-2"><span className="font-mono text-xs text-slate-800">{r.mode}</span></td>
                                        <td className="px-4 py-2 text-slate-600">{r.mediator_name}<span className="text-slate-400"> · {r.party_name}</span></td>
                                        <td className="px-4 py-2 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Input type="number" step="0.01" min="0" max="100" data-testid={`mode-rate-${r.mode}`} value={r.rate ?? ""} disabled={!r.enabled} onChange={(e) => setModeRow(i, { rate: e.target.value })} className="h-8 w-24 text-right" />
                                                <span className="text-slate-400">%</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2">
                                            <Input type="date" data-testid={`mode-eff-${r.mode}`} value={r.effective_from || ""} disabled={!r.enabled} onChange={(e) => setModeRow(i, { effective_from: e.target.value })} className="h-8 w-36" />
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <button type="button" data-testid={`mode-delete-${r.mode}`} onClick={() => deleteMode(r)} title="Remove this payment mode from the mediator" className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600">
                                                <Trash size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {rows.length === 0 && !loading ? (
                <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                    <EmptyState icon={Handshake} title="No mediators" description="Add a mediator to start tracking monthly commission." />
                </div>
            ) : (
                <div className="space-y-5">
                    {rows.map((m) => (
                        <div key={m.id} data-testid={`mcr-mediator-${m.id}`} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-display text-lg font-bold text-slate-900">{m.name}</span>
                                        <span className="text-xs text-slate-500">{m.party_name}</span>
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {(m.mode_names && m.mode_names.length ? m.mode_names : (m.mode_name ? [m.mode_name] : [])).map((mn) => (
                                            <span key={mn} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-700">{mn}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="rounded bg-teal-100 px-2 py-0.5 text-[11px] font-semibold text-teal-800">Current rate {Number(m.commission_rate || 0).toFixed(2)}%</span>
                                    {Number(m.commission_rate || 0) <= 0 && (
                                        <span data-testid={`mcr-rate-warning-${m.id}`} className="rounded bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">Commission rate not set — please edit</span>
                                    )}
                                    <button onClick={() => toggleActive(m)} data-testid={`mcr-toggle-${m.id}`}
                                        className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${m.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                                        {m.active ? "Active" : "Inactive"}
                                    </button>
                                    <button onClick={() => openPartyMgr(m)} data-testid={`mcr-parties-${m.id}`} className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-800 hover:bg-indigo-100" title="Choose which parties this commission applies to"><UsersThree size={13} /> Parties{(m.applies_to_parties && m.applies_to_parties.length) ? ` (${m.applies_to_parties.length})` : ""}</button>
                                    <button onClick={() => openAddCommission(m)} data-testid={`mcr-add-commission-${m.id}`} className="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-2 py-1 text-[11px] font-semibold text-teal-800 hover:bg-teal-100" title="Add a manual commission entry"><Coins size={13} /> Add Commission</button>
                                    <button onClick={() => openEdit(m)} data-testid={`mcr-edit-${m.id}`} className="rounded p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-700" title="Edit"><PencilSimple size={15} /></button>
                                    <button onClick={() => setRateHist(m)} data-testid={`mcr-ratehist-${m.id}`} className="rounded p-1.5 text-slate-500 hover:bg-violet-50 hover:text-violet-700" title="Rate history"><ClockCounterClockwise size={15} /></button>
                                    <button onClick={() => removeMediator(m)} data-testid={`mcr-delete-${m.id}`} className="rounded p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-700" title="Remove from commission tracking"><Trash size={15} /></button>
                                </div>
                            </div>
                            <table className="min-w-full text-sm">
                                <thead className="bg-white text-[10px] uppercase tracking-wider text-slate-500">
                                    <tr>
                                        <th className="px-4 py-2 text-left">Month</th>
                                        <th className="px-4 py-2 text-right">Receipts Total</th>
                                        <th className="px-4 py-2 text-right">Rate %</th>
                                        <th className="px-4 py-2 text-right">Commission</th>
                                        <th className="px-4 py-2 text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {m.months.length === 0 ? (
                                        <tr><td colSpan={5} className="px-4 py-3 text-center text-xs text-slate-400">No commission in the selected range yet.</td></tr>
                                    ) : m.months.map((mo) => (
                                        <tr key={mo.row_key || mo.period} data-testid={`mcr-row-${m.id}-${mo.row_key || mo.period}`} className="hover:bg-slate-50">
                                            <td className="px-4 py-2 font-medium text-slate-800">{mo.label}</td>
                                            <td className="px-4 py-2 text-right font-mono tabular-nums text-slate-700">
                                                <button onClick={() => openDetail(m, mo)} data-testid={`mcr-receipts-${m.id}-${mo.row_key || mo.period}`} className="rounded px-1 text-right underline decoration-dotted underline-offset-2 hover:bg-teal-50 hover:text-teal-800" title="View transactions">{fmt(mo.receipts_total)}</button>
                                            </td>
                                            <td className="px-4 py-2 text-right font-mono tabular-nums text-slate-700">{Number(mo.rate).toFixed(2)}%</td>
                                            <td className="px-4 py-2 text-right font-mono font-semibold tabular-nums text-slate-900">
                                                <button onClick={() => openDetail(m, mo)} data-testid={`mcr-commission-${m.id}-${mo.row_key || mo.period}`} className="rounded px-1 text-right underline decoration-dotted underline-offset-2 hover:bg-teal-50 hover:text-teal-800" title="View transactions">{fmt(mo.commission)}</button>
                                            </td>
                                            <td className="px-4 py-2 text-center">
                                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${mo.posted ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                                                    {mo.posted ? "Posted" : "Pending"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setDetailData(null); } }}>
                <DialogContent data-testid="mcr-detail-dialog" className="max-h-[90vh] w-[96vw] max-w-[96vw] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{detail ? `${detail.name} — ${detail.label}` : "Commission detail"}</DialogTitle>
                        <DialogDescription className="sr-only">Individual transactions that make up this month's receipts and commission.</DialogDescription>
                    </DialogHeader>
                    {detail && (
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                                <span>Receipts: <b className="tabular-nums">{fmt(detailData?.receipts_total ?? 0)}</b></span>
                                <span>Rate: <b>{Number(detail.rate || 0).toFixed(2)}%</b></span>
                                <span>Commission: <b className="tabular-nums text-teal-800">{fmt(detailData?.commission ?? 0)}</b></span>
                                <span data-testid="mcr-detail-reconciled-count">Reconciled: <b className="tabular-nums text-emerald-700">{detailData ? detailData.rows.filter((x) => x.reconciled).length : 0}</b> / {detailData ? detailData.rows.length : 0}</span>
                                <div className="ml-auto flex items-center gap-2">
                                    <Button size="sm" variant="outline" onClick={() => exportDetail("xlsx")} disabled={detailExporting || !detailData || detailData.rows.length === 0} data-testid="mcr-detail-export-xlsx"><DownloadSimple size={14} className="mr-1" /> Excel</Button>
                                    <Button size="sm" variant="outline" onClick={() => exportDetail("pdf")} disabled={detailExporting || !detailData || detailData.rows.length === 0} data-testid="mcr-detail-export-pdf"><FilePdf size={14} className="mr-1" /> PDF</Button>
                                </div>
                            </div>
                            {detailLoading ? (
                                <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
                            ) : !detailData || detailData.rows.length === 0 ? (
                                <div className="py-8 text-center text-sm text-slate-400" data-testid="mcr-detail-empty">No transactions for this month.</div>
                            ) : (
                                <div className="overflow-x-auto rounded-lg border border-slate-200">
                                    <table className="w-full text-xs">
                                        <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                                            <tr>
                                                <th className="whitespace-nowrap px-2 py-2 text-center font-medium" title="Reconciled">✓</th>
                                                <th className="whitespace-nowrap px-2 py-2 font-medium">Date</th>
                                                <th className="whitespace-nowrap px-2 py-2 font-medium">Party Name</th>
                                                <th className="whitespace-nowrap px-2 py-2 font-medium">UTR</th>
                                                <th className="whitespace-nowrap px-2 py-2 font-medium">Mode</th>
                                                <th className="whitespace-nowrap px-2 py-2 text-right font-medium">Amount</th>
                                                <th className="whitespace-nowrap px-2 py-2 text-right font-medium">Commission</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {detailData.rows.map((r, i) => (
                                                <tr key={i} className={r.reconciled ? "bg-emerald-50/60" : "hover:bg-slate-50"} data-testid="mcr-detail-row">
                                                    <td className="whitespace-nowrap px-2 py-2 text-center">
                                                        <input type="checkbox" data-testid={`mcr-detail-reconcile-${r.id}`} checked={!!r.reconciled} onChange={() => toggleReconcile(r)} className="h-4 w-4 cursor-pointer accent-emerald-600" />
                                                    </td>
                                                    <td className="whitespace-nowrap px-2 py-2 text-slate-600">{r.date ? r.date.split("-").reverse().join("-") : "—"}</td>
                                                    <td className="whitespace-nowrap px-2 py-2 font-medium text-slate-900">{r.party_name}</td>
                                                    <td className="whitespace-nowrap px-2 py-2 font-mono text-slate-600">{r.utr || "N/A"}</td>
                                                    <td className="whitespace-nowrap px-2 py-2 text-slate-700">{r.mode || "—"}</td>
                                                    <td className="whitespace-nowrap px-2 py-2 text-right font-mono tabular-nums text-slate-900">{fmt(r.amount)}</td>
                                                    <td className="whitespace-nowrap px-2 py-2 text-right font-mono tabular-nums text-teal-800">{fmt(r.commission)}</td>
                                                </tr>
                                            ))}
                                            <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold" data-testid="mcr-detail-total-row">
                                                <td className="px-2 py-2"></td>
                                                <td className="px-2 py-2"></td>
                                                <td className="whitespace-nowrap px-2 py-2 text-slate-900">TOTAL</td>
                                                <td className="px-2 py-2"></td>
                                                <td className="px-2 py-2"></td>
                                                <td className="whitespace-nowrap px-2 py-2 text-right font-mono tabular-nums text-slate-900" data-testid="mcr-detail-total-amount">{fmt(detailData.receipts_total)}</td>
                                                <td className="whitespace-nowrap px-2 py-2 text-right font-mono tabular-nums text-teal-900" data-testid="mcr-detail-total-commission">{fmt(detailData.commission)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="mcr-dialog" className="max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Mediator" : "Add Mediator"}</DialogTitle>
                        <DialogDescription className="sr-only">Create or edit a mediator, its linked party, commission rate and the transaction modes that trigger commission.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Mediator Name *</Label>
                            <Input data-testid="mcr-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Jitu" className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Linked Party (ledger) *</Label>
                            <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                                <SelectTrigger data-testid="mcr-form-party" className="mt-1.5"><SelectValue placeholder="Select party" /></SelectTrigger>
                                <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Commission Rate (%) *</Label>
                            <Input data-testid="mcr-form-rate" type="number" step="0.01" min="0" max="100" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} placeholder="0.35" className="mt-1.5" />
                            <p className="mt-1 text-[11px] text-slate-500">Rate changes apply from the effective date forward — already-posted months are never recalculated.</p>
                        </div>
                        {editing && (
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Effective From Date</Label>
                                <Input data-testid="mcr-form-effective-from" type="date" value={form.rate_effective_from} onChange={(e) => setForm({ ...form, rate_effective_from: e.target.value })} className="mt-1.5" />
                                <p className="mt-1 text-[11px] text-slate-500">The old rate stays applied to transactions before this date; the new rate applies on/after it.</p>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Linked Transaction Modes *</Label>
                            <p className="mb-1.5 mt-1 text-[11px] text-slate-500">Commission is calculated on this mediator's receipts recorded under any of these "Recd From" modes.</p>
                            <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
                                {modes.length ? modes.map((mn) => {
                                    const owner = modeOwner[mn.toLowerCase()];
                                    return (
                                    <label key={mn} className="flex items-center gap-2 text-sm">
                                        <input type="checkbox" data-testid={`mcr-form-mode-${mn}`} checked={form.mode_names.includes(mn)}
                                            onChange={() => { if (!form.mode_names.includes(mn) && owner) toast.warning(`"${mn}" is already linked to ${owner} — a mode can belong to only one mediator.`); toggleMode(mn); }} />
                                        <span>{mn}</span>
                                        {owner && <span data-testid={`mcr-form-mode-owner-${mn}`} className="text-[11px] font-medium text-amber-600">· linked to {owner}</span>}
                                    </label>
                                    );
                                }) : <p className="text-[11px] text-slate-400">No payment modes yet. Add them under Settings → Payment Modes.</p>}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="mcr-form-cancel">Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="mcr-form-save">{saving ? "Saving…" : (editing ? "Update" : "Save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!rateHist} onOpenChange={(o) => { if (!o) setRateHist(null); }}>
                <DialogContent data-testid="mcr-ratehist-dialog" className="max-h-[85vh] max-w-lg overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{rateHist ? `Rate History — ${rateHist.name}` : "Rate History"}</DialogTitle>
                        <DialogDescription className="sr-only">Log of all commission rate changes with effective and changed-on dates.</DialogDescription>
                    </DialogHeader>
                    {rateHist && (
                        (rateHist.rate_history || []).length === 0 ? (
                            <div className="py-6 text-center text-sm text-slate-400">No rate changes recorded.</div>
                        ) : (
                            <ul className="divide-y divide-slate-100">
                                {[...(rateHist.rate_history || [])].reverse().map((h, i) => (
                                    <li key={i} className="flex items-center justify-between gap-2 py-2 text-sm" data-testid="mcr-ratehist-row">
                                        <span className="font-mono">
                                            {h.previous_rate != null ? `${Number(h.previous_rate).toFixed(2)}% → ` : ""}
                                            <b className="text-teal-800">{Number(h.rate).toFixed(2)}%</b>
                                        </span>
                                        <span className="text-xs text-slate-500">
                                            effective {(h.effective_from || "").slice(0, 10).split("-").reverse().join("-") || "—"}
                                            {h.changed_on ? ` · changed ${(h.changed_on || "").slice(0, 10).split("-").reverse().join("-")}` : ""}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={!!partyMgr} onOpenChange={(o) => { if (!o) setPartyMgr(null); }}>
                <DialogContent data-testid="mcr-parties-dialog" className="max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Commission applies to{partyMgr ? ` — ${partyMgr.name}` : ""}</DialogTitle>
                        <DialogDescription className="sr-only">Pick which parties this mediator's commission can be charged to, each effective from a date.</DialogDescription>
                    </DialogHeader>
                    {partyMgr && (
                        <div className="space-y-4">
                            <p className="text-[11px] text-slate-500">Commission can only be added for parties listed here, on or after each party's effective date. Leave empty to keep charging only the linked party ({partyMgr.party_name || "—"}).</p>
                            <div className="divide-y divide-slate-100 rounded-md border border-slate-200">
                                {(partyMgr.applies_to_parties || []).length === 0 ? (
                                    <div className="px-3 py-3 text-center text-xs text-slate-400" data-testid="mcr-parties-empty">No parties added — commission applies to the linked party only.</div>
                                ) : (partyMgr.applies_to_parties || []).map((ap) => (
                                    <div key={ap.party_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`mcr-party-row-${ap.party_id}`}>
                                        <span className="font-medium text-slate-800">{ap.party_name || ap.party_id}</span>
                                        <span className="ml-auto text-[11px] text-slate-500">from {ap.effective_from ? ap.effective_from.split("-").reverse().join("-") : "—"}</span>
                                        <button onClick={() => removeParty(ap.party_id)} data-testid={`mcr-party-remove-${ap.party_id}`} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600" title="Remove"><Trash size={14} /></button>
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-2 rounded-md border border-slate-200 p-3">
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Add a party</Label>
                                <Select value={partyPick.party_id} onValueChange={(v) => setPartyPick({ ...partyPick, party_id: v })}>
                                    <SelectTrigger data-testid="mcr-party-select" className="mt-1"><SelectValue placeholder="Select party" /></SelectTrigger>
                                    <SelectContent className="max-h-64">{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                                </Select>
                                <div className="flex items-end gap-2">
                                    <div className="flex-1">
                                        <Label className="text-[11px] text-slate-500">Effective from</Label>
                                        <Input type="date" data-testid="mcr-party-effective" value={partyPick.effective_from} onChange={(e) => setPartyPick({ ...partyPick, effective_from: e.target.value })} className="mt-1" />
                                    </div>
                                    <Button onClick={addParty} disabled={partySaving} data-testid="mcr-party-add"><Plus size={14} className="mr-1" /> {partySaving ? "Adding…" : "Add"}</Button>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPartyMgr(null)} data-testid="mcr-parties-close">Done</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={commOpen} onOpenChange={(o) => { setCommOpen(o); if (!o) setCommForm(null); }}>
                <DialogContent data-testid="mcr-commission-dialog">
                    <DialogHeader>
                        <DialogTitle>Add Commission{commForm ? ` — ${commForm.mediatorName}` : ""}</DialogTitle>
                        <DialogDescription className="sr-only">Manually post a commission entry to this mediator's linked party ledger.</DialogDescription>
                    </DialogHeader>
                    {commForm && (
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Charge To Party *</Label>
                                <Select value={commForm.party_id} onValueChange={(v) => setCommForm({ ...commForm, party_id: v })}>
                                    <SelectTrigger data-testid="mcr-comm-party" className="mt-1.5"><SelectValue placeholder="Select party" /></SelectTrigger>
                                    <SelectContent>
                                        {(commForm.allowed && commForm.allowed.length
                                            ? commForm.allowed.map((ap) => <SelectItem key={ap.party_id} value={ap.party_id}>{ap.party_name}{ap.effective_from ? ` (from ${ap.effective_from.split("-").reverse().join("-")})` : ""}</SelectItem>)
                                            : <SelectItem value={commForm.linkedPartyId}>{commForm.linkedPartyName || "Linked party"}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {!(commForm.allowed && commForm.allowed.length) && (
                                    <p className="mt-1 text-[11px] text-slate-500">No party list set — charging the linked party. Use <b>Parties</b> to restrict to specific parties.</p>
                                )}
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Commission Amount (₹) *</Label>
                                <Input data-testid="mcr-comm-amount" type="number" step="0.01" min="0" value={commForm.amount} onChange={(e) => setCommForm({ ...commForm, amount: e.target.value })} placeholder="e.g. 500" className="mt-1.5" autoFocus />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Date</Label>
                                <Input data-testid="mcr-comm-date" type="date" value={commForm.date} onChange={(e) => setCommForm({ ...commForm, date: e.target.value })} className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">UTR / Reference</Label>
                                <Input data-testid="mcr-comm-utr" value={commForm.utr} onChange={(e) => setCommForm({ ...commForm, utr: e.target.value })} className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Remark</Label>
                                <Input data-testid="mcr-comm-remark" value={commForm.remark} onChange={(e) => setCommForm({ ...commForm, remark: e.target.value })} className="mt-1.5" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setCommOpen(false); setCommForm(null); }} data-testid="mcr-comm-cancel">Cancel</Button>
                        <Button onClick={saveCommission} disabled={commSaving} data-testid="mcr-comm-save">{commSaving ? "Saving…" : "Add Commission"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={modeAddOpen} onOpenChange={setModeAddOpen}>
                <DialogContent data-testid="mode-add-dialog">
                    <DialogHeader>
                        <DialogTitle>Add Payment Mode</DialogTitle>
                        <DialogDescription className="sr-only">Add a new payment mode to a mediator with its commission rate and the date from which it applies.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Mediator *</Label>
                            <Select value={modeAddForm.mediator_id} onValueChange={(v) => setModeAddForm({ ...modeAddForm, mediator_id: v })}>
                                <SelectTrigger data-testid="mode-add-mediator" className="mt-1.5"><SelectValue placeholder="Select mediator" /></SelectTrigger>
                                <SelectContent>{mediatorOpts.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Payment Mode *</Label>
                            {(() => {
                                const linked = modeSettings.filter((r) => r.mediator_id === modeAddForm.mediator_id).map((r) => r.mode);
                                const available = modes.filter((m) => !linked.includes(m));
                                return (
                                    <Select value={modeAddForm.mode} onValueChange={(v) => setModeAddForm({ ...modeAddForm, mode: v })}>
                                        <SelectTrigger data-testid="mode-add-name" className="mt-1.5"><SelectValue placeholder="Select a payment mode" /></SelectTrigger>
                                        <SelectContent>
                                            {available.length === 0
                                                ? <div className="px-3 py-2 text-xs text-slate-500">All payment modes are already linked to this mediator</div>
                                                : available.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                );
                            })()}
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Commission Rate (%) *</Label>
                            <Input data-testid="mode-add-rate" type="number" step="0.01" min="0" max="100" value={modeAddForm.rate} onChange={(e) => setModeAddForm({ ...modeAddForm, rate: e.target.value })} placeholder="0.75" className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Effective From *</Label>
                            <Input data-testid="mode-add-eff" type="date" value={modeAddForm.effective_from} onChange={(e) => setModeAddForm({ ...modeAddForm, effective_from: e.target.value })} className="mt-1.5" />
                            <p className="mt-1 text-[11px] text-slate-500">Commission at this rate applies to receipts on/after this date. Earlier receipts keep any earlier rate.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setModeAddOpen(false)} data-testid="mode-add-cancel">Cancel</Button>
                        <Button onClick={saveModeAdd} disabled={modeAddSaving} data-testid="mode-add-save">{modeAddSaving ? "Saving…" : "Add Mode"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
